"use client"

/**
 * The Leo spotted wash — one implementation, driven by Appearance.
 *
 * Leo appearance has a **Spotted card** tab with size, opacity and X/Y
 * placement for each of three blobs, and those controls are only real if
 * every surface that draws the wash draws it the same way. The queue card
 * had grown its own copy of this markup with different offsets and a
 * different clip box, so the sliders moved its blobs to somewhere other
 * than the panel promised. Both now render this.
 *
 * Reads the app-owned store the Appearance panel writes to. The package
 * prefs cannot hold these keys — it persists only its own schema and drops
 * the rest — which is why the sliders used to do nothing. See
 * `lib/leo-spotted-card-prefs`.
 */

import { spottedCardBlobSlots } from "@/lib/leo-ambience"
import { useSpottedCardBlobPrefs } from "@/lib/leo-spotted-card-prefs"
import { cn } from "@/lib/utils"

export function LeoSpottedBlobLayer({
  /** Settled cards use the success green. Open findings keep the Leo wash. */
  wash = "brand",
  className,
}: {
  wash?: "brand" | "success"
  className?: string
}) {
  const blobs = spottedCardBlobSlots(useSpottedCardBlobPrefs())

  return (
    <div
      aria-hidden
      data-intensity="high"
      data-wash={wash === "success" ? "success" : undefined}
      className={cn(
        "leo-ai-blob-layer leo-ai-blob-layer--panel-empty leo-ai-blob-root",
        "pointer-events-none absolute inset-0 z-0 overflow-hidden",
        // Callers that park the layer outside a card pass overflow-visible:
        // the blobs rest below their box, so clipping removes them.
        className,
      )}
    >
      {blobs.map((blob, index) => (
        <div
          key={index}
          className={cn(
            "leo-ai-blob absolute rounded-full",
            index === 0 && "leo-ai-blob-1 left-[2%] blur-3xl",
            index === 1 && "leo-ai-blob-2 left-[34%] blur-3xl",
            index === 2 && "leo-ai-blob-3 right-[2%] blur-2xl",
          )}
          style={{
            width: blob.size,
            height: blob.size,
            opacity: blob.opacity,
            // The resting offsets the Appearance preview is calibrated
            // against. `offsetY` moves the blob from here, so changing
            // these numbers moves what every slider means.
            bottom: (index === 0 ? -64 : index === 1 ? -56 : -40) - blob.offsetY,
            transform: `translateX(${blob.offsetX}px)`,
          }}
        />
      ))}
    </div>
  )
}
